import { ChangeDetectionStrategy, Component, DestroyRef, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { AbstractListViewService, ListViewGridMessages, MasterListViewComponent } from '@examdojo/core/list-view';
import { distinctUntilArrayChanged } from '@examdojo/core/rxjs';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { TranslocoPipe } from '@jsverse/transloco';
import { RowClickedEvent, SelectionChangedEvent } from 'ag-grid-community';
import { combineLatest, filter, of, startWith, take, tap } from 'rxjs';
import { ListViewExternalFilteringComponent } from './filter/list-view-external-filtering.component';
import { ListViewExternalSortingComponent } from './filter/list-view-external-sorting.component';
import { AbstractSimpleAgGridService } from './list-view-ag-grid.service';

@Component({
  selector: 'dojo-list-view',
  imports: [
    TranslocoPipe,
    ListViewExternalSortingComponent,
    MasterListViewComponent,
    ListViewExternalFilteringComponent,
  ],
  templateUrl: './list-view.component.html',
  styleUrls: ['./list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListViewComponent implements OnInit {
  constructor(
    private readonly listViewAgGridService: AbstractSimpleAgGridService,
    private readonly listViewService: AbstractListViewService,
    private readonly destroyRef: DestroyRef,
  ) {}
  readonly messages = input<ListViewGridMessages>();

  readonly formCtrl = input<FormControl<Array<string | number>>>();

  readonly showToolbarActions = input<boolean>(true);

  readonly gridOptions = this.listViewAgGridService.gridOptions;
  readonly columnDefs = this.listViewAgGridService.columnDefs;

  readonly rowClicked = output<RowClickedEvent>();
  readonly selected = output<unknown[]>();

  readonly numberOfSelectedEntries = signal<number>(0);

  onGridSelectionChanged(event: SelectionChangedEvent) {
    if (event.source === 'api') {
      return;
    }

    const formCtrl = this.formCtrl();
    if (formCtrl) {
      const selected = event.api.getSelectedRows().map((row) => row.id);

      formCtrl.setValue(selected);
      this.numberOfSelectedEntries.set(event.api.getSelectedRows().length);

      this.selected.emit(selected);
    }
  }

  onGridRowClicked(event: RowClickedEvent) {
    this.rowClicked.emit(event);
  }

  ngOnInit() {
    this.syncInitialFormValueToGridSelection().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private syncInitialFormValueToGridSelection() {
    const formCtrl = this.formCtrl();
    if (!formCtrl) {
      return of({});
    }
    return combineLatest([
      this.listViewService.gridApi$.pipe(filter(isNotNullish)),
      formCtrl.valueChanges.pipe(startWith(formCtrl.value), distinctUntilArrayChanged()),
    ]).pipe(
      take(1),
      tap(([gridApi, value]) => {
        if (!value?.length) {
          return;
        }

        gridApi.forEachNode((node) => {
          if (value.includes(node.data?.id)) {
            node.setSelected(true, false, 'api');
          }
        });
        this.numberOfSelectedEntries.set(value.length);

        // need to redraw the custom header to display the correct selected count
        const row = gridApi.getDisplayedRowAtIndex(0);
        gridApi.redrawRows({ rowNodes: row ? [row] : [] });
      }),
    );
  }
}
